import { Metamodel } from '../base/model';
import { IModel } from '../base/annotation';
import { LinkedService } from '../linkedservice/linkedservice';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { validateAKVLinkedserviceReference } from 'app/shared/utils/linkedservice-validation-util';
import { format } from 'app/shared/utils/string.util';
import { Credential, dynamicsAuthTypes, IAttribute, IViolation, shellStringResources } from 'shell-models-lib';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { GlobalVariables } from 'app/services/globals';

export const dynamicsDeploymentTypes = {
    Online: 'Online',
    OnPremisesWithIfd: 'OnPremisesWithIfd'
};

export const servicePrincipalCredentialTypes = {
    ServicePrincipalKey: 'ServicePrincipalKey',
    ServicePrincipalCert: 'ServicePrincipalCert'
};

@Metamodel.model({
    name: adfModelTypeMap.dynamics.linkedServiceType,
    display: [
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Dynamics_DisplayText),
        format(stringResources.ModelName_LinkedServicesTemplate, stringResources.Dynamics_DisplayText)
    ]
})
export class DynamicsLinkedService extends LinkedService {

    public static readonly MODEL: IModel;
    public static readonly SERVICE_URL: IAttribute;
    public static readonly ORGANIZATION_NAME: IAttribute;
    public static readonly HOST_NAME: IAttribute;
    public static readonly PASSWORD: IAttribute;
    public static readonly PORT: IAttribute;
    public static readonly USER_NAME: IAttribute;
    public static readonly SERVICE_PRINCIPLE_ID: IAttribute;
    public static readonly SERVICE_PRINCIPLE_KEY: IAttribute;
    public static readonly SERVICE_PRINCIPLE_CERT: IAttribute;
    public static readonly DOMAIN: IAttribute;

    @Metamodel.attribute({
        display: stringResources.ModelName_DeploymentType
    })
    public deploymentType?: string;


    @Metamodel.attribute({
        display: stringResources.DomainLabel,
        association: 'DOMAIN',
        templatable: true,
    })
    public domain?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_ServiceUrl,
        association: 'SERVICE_URL',
        templatable: true,
    })
    public serviceUri?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_OrganizationName,
        association: 'ORGANIZATION_NAME',
        templatable: true,
    })
    public organizationName?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_Host,
        association: 'HOST_NAME',
        templatable: true
    })
    public hostName?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_Port,
        association: 'PORT',
        templatable: true,
    })
    public port?: number = 443;

    @Metamodel.attribute({
        display: stringResources.ModelName_AuthenticationType
    })
    public authenticationType?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_UserName,
        association: 'USER_NAME',
        templatable: true,
    })
    public username?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_Password,
        association: 'PASSWORD',
        templatable: true,
        constraint: {
            validator: validateAKVLinkedserviceReference
        },
        accepts: ['String']
    })
    public password?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: stringResources.ModelName_ServicePrincipalID
    })
    public servicePrincipalCredentialType?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_ServicePrincipalID,
        association: 'SERVICE_PRINCIPLE_ID',
        templatable: true
    })
    public servicePrincipalId?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_AuthenticationType,
        association: 'SERVICE_PRINCIPLE_KEY',
        templatable: true,
        constraint: {
            validator: validateAKVLinkedserviceReference
        },
        accepts: ['String']
    })
    public servicePrincipalKey?: string | AzureKeyVault;

    @Metamodel.attribute({
        display: stringResources.ModelName_AuthenticationType,
        association: 'SERVICE_PRINCIPLE_CERT',
        templatable: true,
        constraint: {
            validator: validateAKVLinkedserviceReference
        }
    })
    public servicePrincipalCert?: AzureKeyVault;

    @Metamodel.attribute({
        display: stringResources.AuthenticationType_UseCredentials,
        reference: true,
        constraint: {
            validator: DynamicsLinkedService.validateCredential
        }
    })
    public credential?: Credential;

    constructor(name?: string, description?: string) {
        super(name, description);
    }

    protected static validateCredential(linkedservice: DynamicsLinkedService, referencedCredential: Credential): IViolation[] | undefined {
        if ((linkedservice?.authenticationType === dynamicsAuthTypes.ManagedIdentity
            || (GlobalVariables.enableConnectorUserAuthCred && linkedservice?.authenticationType === dynamicsAuthTypes.UserAuth))
            && !referencedCredential
        ) {
            return [{
                context: linkedservice,
                message: () => shellStringResources.Error_InvalidCredentialReferenced
            }];
        }
        return undefined;
    }
}
